import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	ArrowUpOutline,
	Box,
	Button,
	ChatAlt2Outline,
	CheckOutline,
	CheveronSmallDownOutline,
	ClockOutline,
	DotsHorizontalOutline,
	DuplicateOutline,
	Flex,
	GlobeAltOutline,
	HStack,
	IconButton,
	Input,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	MicrophoneOutline,
	PencilAltOutline,
	PencilOutline,
	StopSolid,
	Text,
	Textarea,
	Tooltip,
	TrashOutline,
	useDisclosure,
	useToast,
	XOutline,
} from '@bonusly/design-system';

import { useBizyFeedbackMutation, useBizyTitleMutation } from '../../queries/bizy';
import {
	bizyUserConversationsQueryKey,
	useBizyDeleteConversationMutation,
	useBizyUpdateConversationMutation,
	useBizyUserConversation,
	useBizyUserConversations,
} from '../../queries/bizy-conversations';
import { Bizy, type BizyState } from '../bizy';
import { BizyAmbientGlow } from './bizy-ambient-glow';
import { BizySidePanelHeader } from './bizy-side-panel-header';
import { BizySidePanelSuggestedPrompts } from './bizy-side-panel-suggested-prompts';
import { BizyFeedbackModal } from './components/bizy-feedback-modal';
import { BizyMessageFeedback } from './components/bizy-message-feedback';
import { BizyNotesEditMessage } from './components/bizy-notes-edit-message';
import { BizyStreamingMessage } from './components/bizy-streaming-message';
import { useBizySidePanelChat } from './hooks/use-bizy-side-panel-chat';
import { useBizyVoiceInput } from './hooks/use-bizy-voice-input';
import { generateConversationTitle, getActiveConversationTitle } from './lib/conversation-title';
import { getCachedTitle, removeCachedTitle, setCachedTitle } from './lib/conversation-title-cache';
import { getToolProgressKey, isNotesEdit } from './lib/notes-edit-detection';
import { clearBizySession } from './lib/session-storage';
import { getRandomPromptKeys } from './lib/suggested-prompts';
import { useBizyPageContextStore } from './store/use-bizy-page-context-store';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';
import { useBizyWidgetStore } from './store/use-bizy-widget-store';

const chatActionIconStyles = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

function formatMessageTime(raw: Date | string): string {
	const date = raw instanceof Date ? raw : new Date(raw);
	const now = new Date();
	const isToday = date.toDateString() === now.toDateString();
	if (isToday) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
	return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function UserMessageActions({ content, timestamp }: { content: string; timestamp?: Date }) {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const handleCopy = useCallback(() => {
		navigator.clipboard
			.writeText(content)
			.then(() => {
				setCopied(true);
				if (timeoutRef.current) clearTimeout(timeoutRef.current);
				timeoutRef.current = setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {
				toast({ title: t('feedback.copyFailed'), status: 'error', duration: 2000 });
			});
	}, [content, toast, t]);

	return (
		<Flex align="center" gap="4px" justifyContent="flex-end">
			{timestamp && (
				<Text fontSize="xs" color="text.softest" mr={1}>
					{formatMessageTime(timestamp)}
				</Text>
			)}
			<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
				<IconButton
					aria-label={t('feedback.copy')}
					icon={copied ? <CheckOutline boxSize={5} /> : <DuplicateOutline boxSize={5} />}
					{...chatActionIconStyles}
					onClick={handleCopy}
				/>
			</Tooltip>
		</Flex>
	);
}

const RECENT_CONVERSATIONS_LIMIT = 5;

interface BizySidePanelChatViewProps {
	onOpenHistory: () => void;
	resetSignal?: number;
}

export function BizySidePanelChatView({ onOpenHistory, resetSignal = 0 }: BizySidePanelChatViewProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const toast = useToast();
	const { mutate: submitFeedback } = useBizyFeedbackMutation();
	const { mutate: deleteConversation } = useBizyDeleteConversationMutation();
	const { mutate: updateConversation } = useBizyUpdateConversationMutation();
	const { mutate: generateTitle } = useBizyTitleMutation();
	const pageContext = useBizyPageContextStore((state) => state.context);
	const pageType = pageContext.pageType;
	const {
		messages,
		conversationId,
		isLoading,
		isTimedOut,
		isOtherTabLoading,
		bizyEmotion,
		activeToolCalls,
		sendMessage,
		resetChat,
		retryLastMessage,
		updateMessageFeedback,
	} = useBizySidePanelChat();
	const setResume = useBizyWidgetStore((s) => s.setResume);
	const queryClient = useQueryClient();

	const [inputValue, setInputValue] = useState('');
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
	const [loadConversationId, setLoadConversationId] = useState<string | undefined>();
	const [titleOverride, setTitleOverride] = useState<string | undefined>();
	const [isRenaming, setIsRenaming] = useState(false);
	const [isBizyHovered, setIsBizyHovered] = useState(false);
	const [isInputFocused, setIsInputFocused] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const lastUserMsgRef = useRef<HTMLDivElement>(null);
	const shouldScrollRef = useRef(false);
	const renameInputRef = useRef<HTMLInputElement>(null);
	const prevResetSignalRef = useRef(resetSignal);

	const handleTranscript = useCallback((text: string) => {
		if (inputRef.current) {
			// Write to the DOM element first so scrollHeight reflects the new
			// content before we measure. setInputValue only schedules a React
			// re-render; the DOM still has the old value at measurement time.
			inputRef.current.value = text;
			inputRef.current.style.height = 'auto';
			inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
		}
		setInputValue(text);
	}, []);
	const handleVoiceStart = useCallback(() => {
		track(AnalyticsEvent.Bizy.VoiceInputUsed, { path: window.location.pathname });
	}, [track]);
	const handleVoiceError = useCallback(
		(reason: string) => {
			track(AnalyticsEvent.Bizy.VoiceInputError, { reason, path: window.location.pathname });
		},
		[track]
	);
	const {
		isRecording: isVoiceRecording,
		isStarting: isVoiceStarting,
		startRecording: startVoice,
		stopRecording: stopVoice,
	} = useBizyVoiceInput({ onTranscript: handleTranscript, onStart: handleVoiceStart, onError: handleVoiceError });

	useEffect(() => {
		if (resetSignal <= 0 || resetSignal === prevResetSignalRef.current) return;
		prevResetSignalRef.current = resetSignal;
		if (isVoiceRecording) void stopVoice();
		resetChat();
		setInputValue('');
		setTitleOverride(undefined);
		setIsRenaming(false);
	}, [isVoiceRecording, resetChat, resetSignal, stopVoice]);

	const lastUserMsgIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'user') return i;
		}
		return -1;
	}, [messages]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		const inner = container?.firstElementChild as HTMLElement | null;
		if (!container || !inner) return;

		if (shouldScrollRef.current) {
			shouldScrollRef.current = false;
			const el = lastUserMsgRef.current;
			if (el) {
				inner.style.paddingBottom = `${container.clientHeight}px`;
				const scrollTarget = el.offsetTop - 36;
				container.scrollTo({ top: scrollTarget, behavior: 'instant' });
			}
		}

		const currentPadding = parseFloat(inner.style.paddingBottom) || 0;
		if (currentPadding > 0) {
			const contentHeight = container.scrollHeight - currentPadding;
			const maxScrollWithoutPadding = contentHeight - container.clientHeight;
			const needed = Math.max(0, container.scrollTop - maxScrollWithoutPadding);
			inner.style.paddingBottom = `${needed}px`;
		}
	}, [messages]);

	const { data: recentData } = useBizyUserConversations({ per_page: RECENT_CONVERSATIONS_LIMIT });
	const recentConversations = useMemo(() => (recentData?.[0] ?? []).slice(0, RECENT_CONVERSATIONS_LIMIT), [recentData]);

	const { data: loadedConversation } = useBizyUserConversation(loadConversationId);
	const titleSetByLoadRef = useRef(false);
	useEffect(() => {
		if (!loadedConversation || !loadConversationId) return;
		setResume(loadConversationId, loadedConversation.messages);
		setLoadConversationId(undefined);
		const existingTitle = recentConversations.find((c) => c.conversationId === loadConversationId)?.conversationTitle || getCachedTitle(loadConversationId);
		setTitleOverride(existingTitle || undefined);
		setIsRenaming(false);
		titleSetByLoadRef.current = true;
	}, [loadConversationId, loadedConversation, setResume, recentConversations]);

	useEffect(() => {
		if (!conversationId) return;
		if (titleSetByLoadRef.current) {
			titleSetByLoadRef.current = false;
			return;
		}
		const serverTitle = recentConversations.find((c) => c.conversationId === conversationId)?.conversationTitle;
		const cached = serverTitle || getCachedTitle(conversationId);
		if (cached) setTitleOverride(cached);
	}, [conversationId, recentConversations]);

	// Keep the conversations list fresh so the dropdown includes the current chat
	const prevConversationIdRef = useRef<string | undefined>();
	useEffect(() => {
		if (conversationId && conversationId !== prevConversationIdRef.current) {
			prevConversationIdRef.current = conversationId;
			queryClient.invalidateQueries(bizyUserConversationsQueryKey());
		}
	}, [conversationId, queryClient]);

	const firstUserMessage = useMemo(() => messages.find((m) => m.role === 'user')?.content, [messages]);
	const generatedTitle = getActiveConversationTitle(firstUserMessage);
	const conversationTitle = titleOverride ?? generatedTitle;

	// Generate AI title after the first assistant response completes.
	// Skip if the conversation already has a server-side or cached title.
	const hasRequestedTitleRef = useRef<string | undefined>();
	const manuallyRenamedRef = useRef(false);
	const conversationIdRef = useRef(conversationId);
	conversationIdRef.current = conversationId;

	useEffect(() => {
		if (!conversationId || !firstUserMessage || titleOverride) return;
		if (hasRequestedTitleRef.current === conversationId) return;

		const firstAssistant = messages.find((m) => m.role === 'assistant' && m.id);
		if (!firstAssistant) return;

		const requestConversationId = conversationId;
		hasRequestedTitleRef.current = conversationId;
		manuallyRenamedRef.current = false;
		generateTitle(
			{ message: firstUserMessage },
			{
				onSuccess: (data) => {
					if (data.title && !manuallyRenamedRef.current && conversationIdRef.current === requestConversationId) {
						setTitleOverride(data.title);
						setCachedTitle(requestConversationId, data.title);
						updateConversation({ conversationId: requestConversationId, title: data.title });
						track(AnalyticsEvent.BizyAdmin.ConversationTitleGenerated, { path: window.location.pathname });
					}
				},
			}
		);
	}, [conversationId, firstUserMessage, titleOverride, messages, generateTitle, updateConversation, track]);

	const promptName = useMemo(() => {
		if (pageType === '1on1') {
			const name = pageContext.pageName.replace('1:1 with ', '');
			if (name && name !== pageContext.pageName) return name;
		}
		return undefined;
	}, [pageType, pageContext.pageName]);
	const promptChips = useMemo(
		() => getRandomPromptKeys(pageType).map((key) => t(key, { name: promptName })),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pageType, resetSignal, promptName]
	);
	const isPanelOpen = useBizySidePanelStore((state) => state.isOpen);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const isEmptyState = messages.length === 0 && !isLoading;
	const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

	useEffect(() => {
		if (!isPanelOpen && (isVoiceRecording || isVoiceStarting)) {
			void stopVoice();
		}
	}, [isPanelOpen, isVoiceRecording, isVoiceStarting, stopVoice]);

	const lastMessage = messages[messages.length - 1];
	const isStreaming = isLoading && lastMessage?.role === 'assistant' && !lastMessage.id;
	const showThinkingIndicator = isLoading && !isStreaming && lastMessage?.role !== 'assistant';
	const isBizyExcited = isBizyHovered || (isEmptyState && isInputFocused);
	const bizyState: BizyState = isLoading ? 'thinking' : isBizyExcited ? 'excited' : (bizyEmotion ?? 'curious');

	const toolProgressItems = activeToolCalls
		.map(getToolProgressKey)
		.filter((key): key is string => key !== null)
		.map((key) => t(key));

	const lastCompleteAssistantIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const isLastMsg = i === messages.length - 1;
			const isStreamingMsg = isLastMsg && isLoading && messages[i].role === 'assistant';
			if (messages[i].role === 'assistant' && messages[i].id && !isStreamingMsg) return i;
		}
		return -1;
	}, [messages, isLoading]);

	const inputDisabled = isLoading || isOtherTabLoading;

	const handleVoiceToggle = useCallback(async () => {
		if (isVoiceRecording) {
			await stopVoice();
		} else {
			await startVoice();
		}
	}, [isVoiceRecording, startVoice, stopVoice]);

	const handleSend = useCallback(() => {
		if (!inputValue.trim() || inputDisabled) return;
		if (isVoiceRecording) void stopVoice();
		sendMessage(inputValue.trim());
		setInputValue('');
		shouldScrollRef.current = true;
		if (inputRef.current) {
			inputRef.current.style.height = 'auto';
			inputRef.current.focus();
		}
	}, [inputValue, inputDisabled, isVoiceRecording, stopVoice, sendMessage]);

	const handleResetConversation = useCallback(() => {
		if (isVoiceRecording) void stopVoice();
		track(AnalyticsEvent.BizyAdmin.ConversationReset, {
			conversationLength: messages.length,
			path: window.location.pathname,
		});
		resetChat();
		setInputValue('');
		setTitleOverride(undefined);
		setIsRenaming(false);
		inputRef.current?.focus();
	}, [isVoiceRecording, messages.length, resetChat, stopVoice, track]);

	const handleSelectPrompt = useCallback(
		(prompt: string) => {
			sendMessage(prompt);
			shouldScrollRef.current = true;
		},
		[sendMessage]
	);

	const handleStartRename = useCallback(() => {
		setIsRenaming(true);
		setTimeout(() => renameInputRef.current?.select(), 0);
	}, []);

	const handleFinishRename = useCallback(() => {
		setIsRenaming(false);
		const trimmed = titleOverride?.trim();
		if (!trimmed) {
			setTitleOverride(undefined);
			return;
		}
		setTitleOverride(trimmed);
		manuallyRenamedRef.current = true;
		if (conversationId) {
			setCachedTitle(conversationId, trimmed);
			updateConversation({ conversationId, title: trimmed });
			track(AnalyticsEvent.BizyAdmin.ConversationRenamed, { path: window.location.pathname });
		}
	}, [conversationId, titleOverride, updateConversation, track]);

	const deleteDialog = useDisclosure();
	const deleteCancelRef = useRef<HTMLButtonElement>(null);

	const handleConfirmDelete = useCallback(() => {
		if (isVoiceRecording) void stopVoice();
		if (conversationId) {
			deleteConversation(conversationId, {
				onSuccess: () => {
					removeCachedTitle(conversationId);
					clearBizySession();
					resetChat();
					setInputValue('');
					setTitleOverride(undefined);
					setIsRenaming(false);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatSuccess'), status: 'info', duration: 3000 });
					track(AnalyticsEvent.BizyAdmin.ConversationDeleted, { path: window.location.pathname });
				},
				onError: () => {
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatError'), status: 'error', duration: 3000 });
				},
			});
		} else {
			resetChat();
			setInputValue('');
			setTitleOverride(undefined);
			setIsRenaming(false);
			deleteDialog.onClose();
		}
	}, [conversationId, isVoiceRecording, resetChat, deleteConversation, deleteDialog, stopVoice, toast, track, t]);

	const handlePositiveFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.BizyAdmin.FeedbackPositive, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: 'positive' }, { onSuccess: () => updateMessageFeedback(messageId, 'positive') });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleNegativeFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.BizyAdmin.FeedbackNegativeStarted, { path: window.location.pathname });
			setFeedbackMessageId(messageId);
			setIsFeedbackModalOpen(true);
		},
		[track]
	);

	const handleClearFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.BizyAdmin.FeedbackCleared, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: null }, { onSuccess: () => updateMessageFeedback(messageId, undefined) });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleFeedbackModalSubmit = useCallback(
		(category: string, details?: string) => {
			if (!feedbackMessageId) return;
			track(AnalyticsEvent.BizyAdmin.FeedbackNegativeSubmitted, { category, path: window.location.pathname });
			submitFeedback(
				{
					messageId: feedbackMessageId,
					feedback_type: 'negative',
					feedback_category: category,
					feedback_details: details,
				},
				{
					onSuccess: () => {
						updateMessageFeedback(feedbackMessageId, 'negative');
						setIsFeedbackModalOpen(false);
						setFeedbackMessageId(null);
					},
				}
			);
		},
		[feedbackMessageId, submitFeedback, updateMessageFeedback, track]
	);

	const titleElement = isRenaming ? (
		<Input
			ref={renameInputRef}
			value={titleOverride ?? conversationTitle}
			onChange={(e) => setTitleOverride(e.target.value)}
			onBlur={handleFinishRename}
			onKeyDown={(e) => {
				if (e.key === 'Enter') handleFinishRename();
			}}
			size="sm"
			fontSize="md"
			fontWeight="semibold"
			variant="outline"
			px={1.5}
			h="32px"
			borderRadius="md"
			borderColor="primary.fg"
			borderWidth="1.5px"
			_focus={{ borderColor: 'primary.fg', boxShadow: 'none' }}
			autoFocus
		/>
	) : (
		<Menu>
			<MenuButton
				as={Button}
				variant="ghost"
				size="sm"
				px={1.5}
				h="auto"
				minH="28px"
				maxW="100%"
				iconSpacing={1}
				rightIcon={<CheveronSmallDownOutline boxSize={3.5} color="info.icon" flexShrink={0} />}
				_hover={{ bg: 'secondary.bgAlpha' }}
			>
				<Text fontSize="md" fontWeight="semibold" color="text.default" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{conversationTitle}
				</Text>
			</MenuButton>
			<MenuList minW="220px" maxH="320px" overflowY="auto" zIndex="popover">
				{recentConversations.length > 0 && (
					<>
						<Text px={3} py={1.5} fontSize="xs" fontWeight="medium" color="text.softer">
							{t('sidePanel.recentConversations')}
						</Text>
						{recentConversations.map((conversation) => (
							<MenuItem key={conversation.conversationId} onClick={() => setLoadConversationId(conversation.conversationId)}>
								<Text noOfLines={1}>
									{conversation.conversationTitle || getCachedTitle(conversation.conversationId) || generateConversationTitle(conversation.firstUserMessage)}
								</Text>
							</MenuItem>
						))}
						<MenuDivider />
					</>
				)}
				<MenuItem onClick={onOpenHistory} color="text.interactive">
					{t('sidePanel.viewAllHistory')}
				</MenuItem>
			</MenuList>
		</Menu>
	);

	return (
		<Flex direction="column" h="100%">
			<BizySidePanelHeader
				left={titleElement}
				right={
					<HStack spacing={1}>
						<Tooltip label={t('sidePanel.newChat')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.newChat')}
								icon={<PencilAltOutline boxSize={4} />}
								size="sm"
								bg="secondary.bgAlpha"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlphaHover' }}
								onClick={handleResetConversation}
							/>
						</Tooltip>
						<Menu>
							<Tooltip label={t('sidePanel.renameAndMore')} hasArrow={false}>
								<MenuButton
									as={IconButton}
									aria-label={t('sidePanel.renameAndMore')}
									icon={<DotsHorizontalOutline boxSize={4} />}
									size="sm"
									bg="secondary.bgAlpha"
									color="secondary.fg"
									_hover={{ bg: 'secondary.bgAlphaHover' }}
								/>
							</Tooltip>
							<MenuList zIndex="popover">
								<MenuItem onClick={onOpenHistory} icon={<ClockOutline boxSize={5} />}>
									{t('sidePanel.historyTitle')}
								</MenuItem>
								<MenuItem onClick={handleStartRename} icon={<PencilOutline boxSize={5} />}>
									{t('sidePanel.renameChat')}
								</MenuItem>
								<MenuDivider />
								<MenuItem onClick={deleteDialog.onOpen} color="error.fg" icon={<TrashOutline boxSize={5} />}>
									{t('sidePanel.deleteChat')}
								</MenuItem>
							</MenuList>
						</Menu>
						<IconButton
							aria-label="Close Bizy"
							icon={<XOutline boxSize={4} />}
							size="sm"
							bg="secondary.bgAlpha"
							color="secondary.fg"
							_hover={{ bg: 'secondary.bgAlphaHover' }}
							onClick={closePanel}
						/>
					</HStack>
				}
			/>

			<Flex direction="column" flex={1} minH={0}>
				<Box position="relative" flex={1} overflow="hidden">
					<Box
						position="absolute"
						top={0}
						left={0}
						right={0}
						height="32px"
						background="linear-gradient(to bottom, var(--chakra-colors-bg-canvas) 0%, transparent 100%)"
						pointerEvents="none"
						opacity={isScrolledFromTop ? 1 : 0}
						transition="opacity 0.15s ease-in-out"
						zIndex={1}
					/>
					<Box
						ref={scrollContainerRef}
						height="100%"
						overflowY="auto"
						onScroll={(e: React.UIEvent<HTMLDivElement>) => setIsScrolledFromTop(e.currentTarget.scrollTop > 10)}
						sx={{ scrollPaddingTop: '36px' }}
						{...(isEmptyState ? { display: 'flex', flexDirection: 'column' as const } : {})}
					>
						<Flex direction="column" gap={2} px={4} pt={3} flex={isEmptyState ? 1 : undefined} minH={0}>
							{isEmptyState && isPanelOpen && (
								<Flex direction="column" align="center" justify="center" flex={1} py={6}>
									<motion.div
										initial={{ scale: 0, opacity: 0 }}
										animate={{ scale: 1, opacity: 1 }}
										transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.25 }}
										onMouseEnter={() => setIsBizyHovered(true)}
										onMouseLeave={() => setIsBizyHovered(false)}
										style={{ cursor: 'pointer' }}
									>
										<Bizy state={bizyState} size="md" />
									</motion.div>
								</Flex>
							)}
							{messages.map((message, index) => {
								const isUser = message.role === 'user';
								const isLast = index === messages.length - 1;
								const isStreamingThisMessage = isLast && isStreaming;
								const isComplete = !isStreamingThisMessage && !isUser && !!message.id;

								if (isUser) {
									return (
										<Flex
											key={message.key || index}
											ref={index === lastUserMsgIndex ? lastUserMsgRef : undefined}
											direction="column"
											alignSelf="flex-end"
											maxW="85%"
											role="group"
										>
											<Box
												bg="secondary.bgAlpha"
												borderRadius={12}
												borderBottomRightRadius={4}
												px={3}
												py={2}
												border="1px solid"
												borderColor="secondary.bgAlpha"
											>
												<Text fontSize={{ base: 'md', sm: 'sm' }}>{message.content}</Text>
											</Box>
											<Box opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.15s ease-in-out" mt={1}>
												<UserMessageActions content={message.content} timestamp={message.timestamp} />
											</Box>
										</Flex>
									);
								}

								if (!isStreamingThisMessage && isNotesEdit(message)) {
									return (
										<Flex key={message.key || message.id || index} direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
											<BizyNotesEditMessage content={message.content} StreamingMessage={BizyStreamingMessage} />
											{isComplete && message.id && (
												<BizyMessageFeedback
													messageId={message.id}
													content={message.content}
													feedbackType={message.feedbackType}
													onPositiveFeedback={handlePositiveFeedback}
													onNegativeFeedback={handleNegativeFeedback}
													onClearFeedback={handleClearFeedback}
													isLatestReply={index === lastCompleteAssistantIndex}
												/>
											)}
										</Flex>
									);
								}

								const messageToolProgress = isStreamingThisMessage ? toolProgressItems : [];

								return (
									<Flex key={message.key || message.id || index} direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
										<BizyStreamingMessage content={message.content} isComplete={!isStreamingThisMessage} toolProgress={messageToolProgress} />
										{isComplete && message.id && (
											<BizyMessageFeedback
												messageId={message.id}
												content={message.content}
												feedbackType={message.feedbackType}
												onPositiveFeedback={handlePositiveFeedback}
												onNegativeFeedback={handleNegativeFeedback}
												onClearFeedback={handleClearFeedback}
												isLatestReply={index === lastCompleteAssistantIndex}
											/>
										)}
									</Flex>
								);
							})}

							{showThinkingIndicator && (
								<Flex direction="column" gap={0.5} alignSelf="flex-start">
									{toolProgressItems.length > 0 ? (
										toolProgressItems.map((item, i) => (
											<Text
												key={i}
												fontSize="xs"
												fontWeight="medium"
												color="text.softest"
												sx={
													i === toolProgressItems.length - 1
														? { '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } }, animation: 'pulse 1.5s ease-in-out infinite' }
														: { opacity: 0.6 }
												}
											>
												{item}
											</Text>
										))
									) : (
										<Text
											fontSize="xs"
											fontWeight="medium"
											color="text.softest"
											sx={{
												'@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
												animation: 'pulse 1.5s ease-in-out infinite',
											}}
										>
											{t('thinking')}
										</Text>
									)}
								</Flex>
							)}

							{isTimedOut && (
								<Flex alignSelf="flex-start" direction="column" gap={1} pl={1}>
									<Text fontSize="sm" color="text.softer">
										{t('timeout.message')}
									</Text>
									<Button variant="link" size="sm" color="text.interactive" onClick={retryLastMessage} px={0} justifyContent="flex-start">
										{t('timeout.retry')}
									</Button>
								</Flex>
							)}

							{messages.length > 0 && (
								<motion.div
									layout
									transition={{ type: 'spring', stiffness: 200, damping: 25 }}
									style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}
									onMouseEnter={() => setIsBizyHovered(true)}
									onMouseLeave={() => setIsBizyHovered(false)}
								>
									<Bizy state={bizyState} size="md" />
								</motion.div>
							)}
						</Flex>
					</Box>
				</Box>

				<Box px={4} pb="20px" pt={1}>
					{isEmptyState && <BizySidePanelSuggestedPrompts prompts={promptChips} onSelectPrompt={handleSelectPrompt} />}
					{isOtherTabLoading && (
						<Text fontSize="xs" color="text.softest" px={1} pb={1}>
							{t('otherTab.responding')}
						</Text>
					)}
					<Box position="relative">
						<BizyAmbientGlow />
						<Flex direction="column" gap="4px" border="1px solid" borderColor="border.default" borderRadius="12px" p="8px" bg="bg.surface" position="relative">
							<HStack spacing="4px">
								<HStack spacing={1} bg="secondary.bgAlpha" borderRadius="6px" px={1.5} py={0.5}>
									<Box as={pageType === '1on1' ? ChatAlt2Outline : GlobeAltOutline} boxSize={3} color="text.softest" sx={{ '& path': { strokeWidth: 1 } }} />
									<Text fontSize="xs" color="text.softest">
										{pageContext.pageName}
									</Text>
								</HStack>
							</HStack>
							<Flex align="flex-end" gap={2}>
								<Textarea
									ref={inputRef}
									value={inputValue}
									onChange={(e) => {
										setInputValue(e.target.value);
										e.target.style.height = 'auto';
										e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey) {
											e.preventDefault();
											handleSend();
										}
									}}
									onFocus={() => setIsInputFocused(true)}
									onBlur={() => setIsInputFocused(false)}
									placeholder={t('inputPlaceholder')}
									aria-label={t('ariaLabel.chatMessageInput')}
									variant="unstyled"
									fontSize={{ base: 'md', sm: 'sm' }}
									rows={1}
									resize="none"
									minH="32px"
									maxH="120px"
									lineHeight="20px"
									px="4px"
									py="6px"
								/>
								{(!inputValue.trim() || isVoiceRecording || isVoiceStarting) && (
									<Tooltip label={t(isVoiceRecording ? 'voiceInput.stop' : 'voiceInput.start')} hasArrow={false}>
										<IconButton
											aria-label={t(isVoiceRecording ? 'voiceInput.stop' : 'voiceInput.start')}
											icon={isVoiceRecording ? <StopSolid boxSize={4} /> : <MicrophoneOutline boxSize={4} />}
											borderRadius="full"
											size="sm"
											minW={8}
											h={8}
											bg={isVoiceRecording ? 'error.bg' : 'transparent'}
											color={isVoiceRecording ? 'error.fg' : 'text.softest'}
											onClick={handleVoiceToggle}
											isLoading={isVoiceStarting}
											isDisabled={inputDisabled}
											_hover={{ bg: isVoiceRecording ? 'error.bgHover' : 'secondary.bgAlpha' }}
											_disabled={{ opacity: 0.2, cursor: 'not-allowed' }}
										/>
									</Tooltip>
								)}
								<IconButton
									aria-label={t('ariaLabel.sendMessage')}
									icon={<ArrowUpOutline boxSize={4} />}
									borderRadius="full"
									size="sm"
									minW={8}
									h={8}
									bg="secondary.bg"
									color="secondary.fgOnSurface"
									onClick={handleSend}
									isDisabled={!inputValue.trim() || inputDisabled}
									_hover={{ bg: 'secondary.bgHover' }}
									_disabled={{ opacity: 0.2, cursor: 'not-allowed' }}
								/>
							</Flex>
						</Flex>
					</Box>
				</Box>
			</Flex>

			<BizyFeedbackModal
				isOpen={isFeedbackModalOpen}
				onClose={() => {
					track(AnalyticsEvent.BizyAdmin.FeedbackNegativeCancelled, { path: window.location.pathname });
					setIsFeedbackModalOpen(false);
					setFeedbackMessageId(null);
				}}
				onSubmit={handleFeedbackModalSubmit}
			/>

			<AlertDialog leastDestructiveRef={deleteCancelRef as RefObject<HTMLButtonElement>} isOpen={deleteDialog.isOpen} onClose={deleteDialog.onClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.deleteChatTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.deleteChatConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={deleteCancelRef} variant="outline" colorScheme="secondary" onClick={deleteDialog.onClose}>
								{t('sidePanel.cancel')}
							</Button>
							<Button variant="solid" colorScheme="danger" onClick={handleConfirmDelete}>
								{t('sidePanel.deleteChat')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}
